import React from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { FormErrors, SupportFormValues } from '../types/support';
import { loadSupportDraft, saveSupportDraft } from '../entities/support/model/supportDraft';

const initialValues: SupportFormValues = {
    email: '',
    name: '',
    project: '',
    category: '',
    description: '',
    priority: 'normal',
};

const requiredFields = ['email', 'name', 'project', 'category', 'description'] as const;

export interface ResetOptions {
    /** Поля, которые очистка не трогает. */
    keep?: (keyof SupportFormValues)[];
}

export function useSupportForm(defaults?: Partial<SupportFormValues>) {
    /* Черновик читаем в инициализаторе, а не эффектом: так он попадает
       в первый же рендер и не затирается записью ниже. */
    const [values, setValues] = useState<SupportFormValues>(() => ({
        ...initialValues,
        ...defaults,
        ...loadSupportDraft(),
    }));

    const [errors, setErrors] = useState<FormErrors>({});

    useEffect(() => {
        saveSupportDraft(values);
    }, [values]);

    const setField = useCallback(
        <K extends keyof SupportFormValues>(key: K, value: SupportFormValues[K]) => {
            setValues((prev) => ({ ...prev, [key]: value }));
            setErrors((prev) => ({ ...prev, [key]: undefined }));
        },
        [],
    );

    const validate = useCallback(() => {
        const found: FormErrors = {};

        if (!values.email.trim()) found.email = 'Укажите почту';
        else if (!/^\S+@\S+\.\S+$/.test(values.email)) found.email = 'Проверьте формат адреса';
        if (!values.name.trim()) found.name = 'Укажите имя';
        if (!values.project) found.project = 'Выберите проект';
        if (!values.category) found.category = 'Выберите раздел';
        if (!values.description.trim()) found.description = 'Опишите проблему';

        setErrors(found);

        const firstInvalid = requiredFields.find((field) => found[field]) ?? null;
        return { errors: found, firstInvalid };
    }, [values]);

    const reset = useCallback(
        (options?: ResetOptions) => {
            setValues((prev) => {
                const kept = Object.fromEntries(
                    (options?.keep ?? []).map((field) => [field, prev[field]]),
                );

                return { ...initialValues, ...defaults, ...kept };
            });
            setErrors({});
        },
        [defaults],
    );

    return { values, errors, setField, onChange: setField, validate, reset };
}